import { Fragment, useCallback, useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inputClass } from "@/components/FieldInput";
import { PageHeader, Card, StatusBadge } from "@/components/Ui";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function AuditTab() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ action: "", email: "", outcome: "" });
  const [chain, setChain] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/audit", { params: Object.fromEntries(Object.entries(f).filter(([, v]) => v)) }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), [f]);
  useEffect(() => { load(); }, [load]);
  const verify = async () => { try { const { data } = await api.get("/audit/verify"); setChain(data); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Card>
      <div className="p-4 flex flex-wrap gap-2 items-center border-b border-slate-100">
        <input className={`${inputClass} w-48`} placeholder="Azione (es. restore)" value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} data-testid="audit-filter-action" />
        <input className={`${inputClass} w-56`} placeholder="Email utente" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="audit-filter-email" />
        <select className={`${inputClass} w-40`} value={f.outcome} onChange={(e) => setF({ ...f, outcome: e.target.value })} data-testid="audit-filter-outcome">
          <option value="">Tutti gli esiti</option><option value="success">success</option><option value="failure">failure</option><option value="denied">denied</option><option value="blocked">blocked</option>
        </select>
        <div className="flex-1" />
        {chain && (
          <span data-testid="audit-chain-result" className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${chain.valid ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
            {chain.valid ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldAlert className="h-3.5 w-3.5" />}
            {chain.valid ? `Catena integra · ${chain.checked} eventi` : `Catena alterata all'evento #${chain.broken_at_seq}`}
          </span>
        )}
        <Button variant="outline" onClick={verify} data-testid="audit-verify-button">Verifica integrità</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left" data-testid="audit-table">
          <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wider">
            <tr><th className="px-4 py-3">#</th><th className="px-4 py-3">Data</th><th className="px-4 py-3">Utente</th><th className="px-4 py-3">Azione</th><th className="px-4 py-3">Esito</th><th className="px-4 py-3">Severità</th><th className="px-4 py-3">IP</th><th className="px-4 py-3">Hash</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Fragment key={r.id}>
                <tr className="border-b border-slate-100 hover:bg-slate-50/70 cursor-pointer" onClick={() => setOpen(open === r.id ? null : r.id)}>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{r.seq}</td>
                  <td className="px-4 py-2.5 text-xs whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                  <td className="px-4 py-2.5 text-xs">{r.user_email || "sistema"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-800">{r.action}</td>
                  <td className="px-4 py-2.5"><StatusBadge value={r.outcome} /></td>
                  <td className="px-4 py-2.5"><StatusBadge value={r.severity} /></td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{r.ip || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-slate-400">{r.hash?.slice(0, 12)}…</td>
                </tr>
                {open === r.id && (
                  <tr className="bg-slate-50"><td colSpan={8} className="px-4 py-3"><pre className="text-[11px] font-mono whitespace-pre-wrap text-slate-600 max-h-64 overflow-auto">{JSON.stringify({ resource: `${r.resource_type || ""} ${r.resource_id || ""}`, trust_id: r.trust_id, details: r.details, prev_hash: r.prev_hash, hash: r.hash, user_agent: r.user_agent }, null, 2)}</pre></td></tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function AccessTab() {
  const [rows, setRows] = useState([]);
  const [outcome, setOutcome] = useState("");
  useEffect(() => { api.get("/access-logs", { params: outcome ? { outcome } : {} }).then((r) => setRows(r.data)).catch(() => {}); }, [outcome]);
  return (
    <Card>
      <div className="p-4 border-b border-slate-100">
        <select className={`${inputClass} w-48`} value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="access-filter-outcome">
          <option value="">Tutti gli esiti</option><option value="success">Riusciti</option><option value="failure">Falliti</option><option value="denied">Negati</option><option value="blocked">Bloccati</option>
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left" data-testid="access-logs-table">
          <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wider">
            <tr><th className="px-4 py-3">Data</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Evento</th><th className="px-4 py-3">Esito</th><th className="px-4 py-3">Motivo</th><th className="px-4 py-3">IP</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100">
                <td className="px-4 py-2.5 text-xs whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                <td className="px-4 py-2.5 text-xs">{r.email}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{r.action}</td>
                <td className="px-4 py-2.5"><StatusBadge value={r.outcome} /></td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{r.reason || "—"}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{r.ip}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function Logs() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  return (
    <div>
      <PageHeader eyebrow="A.8.15 · A.8.16" title="Registro Audit" subtitle="Log degli eventi append-only con catena di hash SHA-256: ogni alterazione è rilevabile. Log degli accessi riusciti e falliti con IP." />
      <Tabs defaultValue={isAdmin ? "audit" : "access"}>
        <TabsList className="bg-white border border-slate-200 mb-6">
          {isAdmin && <TabsTrigger value="audit" data-testid="logs-tab-audit" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">Registro eventi</TabsTrigger>}
          <TabsTrigger value="access" data-testid="logs-tab-access" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">Log accessi</TabsTrigger>
        </TabsList>
        {isAdmin && <TabsContent value="audit"><AuditTab /></TabsContent>}
        <TabsContent value="access"><AccessTab /></TabsContent>
      </Tabs>
    </div>
  );
}
